import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { MediaAsset } from '@/src/interfaces';
import { Thumbnail } from './Thumbnail';
import { useGalleryContext } from '../GalleryContext';

const ThumbnailSliderWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => mobile && 'background-color: #f4f4f4;'}
  width: 100%;
`;

const ThumbnailSliderContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  user-select: none;
`;

const TrackWrapper = styled.div<{
  trackTranslate: number;
  trackWidth: number;
}>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: row nowrap;
  position: relative;
  will-change: transform;
  touch-action: pan-y;
`;

const ThumbnailsTrackWrapper = styled(TrackWrapper).attrs<{
  trackTranslate: number;
  trackWidth: number;
}>(({ trackTranslate, trackWidth }) => ({
  style: {
    transform: `translate3d(${trackTranslate}px, 0px, 0px)`,
    transition: `transform ${Math.sqrt(1.5 * trackWidth) * 5}ms cubic-bezier(0.22, 0.36, 0.57, 1)`,
  },
}))``;

const ThumbnailsTrack = styled.div<{ trackWidth: number }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: row nowrap;
  position: relative;
  width: ${({ trackWidth }) => (trackWidth > 0 ? `${trackWidth}px;` : '100%;')};
`;

export const updateTranslate = (
  newTranslate: number,
  max: number,
  setTrackTranslate: (value: number) => void,
) => {
  let translateToSet = newTranslate;
  if (newTranslate >= 0) {
    translateToSet = 0;
  } else if (newTranslate * -1 > max) {
    translateToSet = max * -1;
  }
  setTrackTranslate(translateToSet);
};

interface ThumbnailSliderProps {
  images: MediaAsset[];
  name: string;
  // disable eslint no-explicit-any for slider prop
  slider: any; // eslint-disable-line
}

export const ThumbnailSlider: FunctionComponent<ThumbnailSliderProps> = ({
  images,
  name,
  slider,
}): JSX.Element => {
  const [trackWidth, setTrackWidth] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<number>(0);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const [trackTranslate, setTrackTranslate] = useState<number>(0);
  const [isSwipeEnabled, setIsSwipeEnabled] = useState<boolean>(false);
  const [oldSlideIndex, setOldSlideIndex] = useState<number>(0);
  const { getActiveImage, setActiveImage } = useGalleryContext();
  const dispatchGTMEvent = useTracking();

  const track = useRef(null);
  const container = useRef(null);

  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  const thumbnailDimension: number = isMobile ? 60 : 97;
  const thumbnailMargin: number = isMobile ? 8 : 16;

  useEffect(() => {
    if (track?.current?.offsetWidth > container?.current?.offsetWidth) {
      setIsSwipeEnabled(true);
      setTrackWidth(track.current.offsetWidth);
    }
  }, [images, track?.current?.offsetWidth, container?.current?.offsetWidth]);

  useEffect(() => {
    setMaxWidth(trackWidth - container?.current?.offsetWidth);
  }, [trackWidth, container]);

  useEffect(() => {
    if (track?.current?.offsetWidth > container?.current?.offsetWidth) {
      let newTranslate: number = 0;
      if (oldSlideIndex < getActiveImage()) {
        newTranslate = trackTranslate + thumbnailDimension * -1;
      } else if (oldSlideIndex > getActiveImage()) {
        newTranslate = trackTranslate - thumbnailDimension * -1;
      }
      updateTranslate(newTranslate, maxWidth, setTrackTranslate);
      setOldSlideIndex(getActiveImage());
    }
  }, [getActiveImage()]);

  return (
    <ThumbnailSliderWrapper mobile={isMobile} id="thumbnail-slider">
      <ThumbnailSliderContainer data-testid="thumbnail-slider" ref={container}>
        <ThumbnailsTrack
          data-testid="thumbnails-track"
          trackWidth={trackWidth + 1}
          onTouchStart={(event) => {
            if (isMobile && isSwipeEnabled) {
              setTouchStart(event.changedTouches[0].clientX);
            }
          }}
          onTouchMove={(event) => {
            if (isMobile && isSwipeEnabled) {
              const newTranslate = trackTranslate + (event.changedTouches[0].clientX - touchStart);
              updateTranslate(newTranslate, maxWidth, setTrackTranslate);
              setTouchStart(event.changedTouches[0].clientX);
            }
          }}
          onTouchEnd={() => {
            if (isMobile && isSwipeEnabled) {
              setTouchStart(trackTranslate);
            }
          }}
        >
          <ThumbnailsTrackWrapper
            ref={track}
            trackTranslate={trackTranslate}
            trackWidth={trackWidth}
          >
            {images.map(
              (image: MediaAsset, index: number): JSX.Element => (
                <Thumbnail
                  data-testid="thumbnail"
                  image={image}
                  isActive={getActiveImage() === index}
                  isMobile={isMobile}
                  key={image.url}
                  name={name}
                  onClick={(): void => {
                    slider.slickGoTo(index);
                    setActiveImage(index);
                    if (image.type === 'CLIPLISTER') {
                      dispatchGTMEvent({
                        event: 'ga_event',
                        eventAction: 'viewVideo',
                        eventCategory: 'cliplister',
                        eventLabel: 'detailview',
                        eventValue: 'click',
                      });
                    }
                  }}
                  thumbDim={thumbnailDimension}
                  thumbMargin={thumbnailMargin}
                />
              ),
            )}
          </ThumbnailsTrackWrapper>
        </ThumbnailsTrack>
      </ThumbnailSliderContainer>
    </ThumbnailSliderWrapper>
  );
};

export default ThumbnailSlider;
